import React, { Component } from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { getStyle } from '../../../../utils/utils';
import {
  updateOrganizationVehicle as _updateOrganizationVehicle,
  fetchVehicle as _fetchVehicle,
} from '../../../../actions/vehicleActions';
import {
  fetchOrganizationVehicleSettingsChoices as _fetchOrganizationVehicleSettingsChoices,
} from '../../../../actions/organizationVehicleSettingsActions';
import { getLengthValidator } from '../../../../utils/validators';
import { isEquipment, isUnregistered } from '../../../../utils/equipment';
import CBMediaQuery from '../../../shared/CBMediaQuery';
import CBModal from '../../../shared/CBModal';
import CBSpinnerFloating from '../../../shared/CBSpinnerFloating';
import CBInputField from '../../../shared/form_components/CBInputField';
import CBSelectField from '../../../shared/form_components/CBSelectField';
import RadioButtonGroup from '../../../shared/RadioButtonGroup';

const METER_HOURS = 'hours';
const METER_KM = 'km';

/**
 * "Muuta teknisiä tietoja": the record's model and technical data.
 *
 * On a registered vehicle Traficom owns the technical data, so only the model
 * data (year model, model / trim level) is editable. An unregistered asset has
 * no Traficom record, so its identity and technical data are edited here too;
 * those go in the `equipment` block, which the backend ignores unless the
 * record really is unregistered.
 *
 * A list row carries only part of the record. Saving the form built from it
 * would blank every field the row lacks, so the full record is fetched first.
 *
 * Ajoneuvo-/laitetyyppi is the organization's own grouping, for vehicles and
 * equipment alike. Its pick-list is admin/manager data (as toimipiste in the
 * vehicle info dialog); everyone else sees the current value as text.
 */
class EditTechnicalDataModal extends Component {
  constructor(props) {
    super(props);
    const { vehicle } = props;
    const hasFullData = this.hasFullData(vehicle);
    this.state = {
      formData: hasFullData ? this.buildFormData(vehicle) : null,
      formValid: {},
      loading: !hasFullData,
    };
  }

  componentDidMount() {
    const { vehicle, fetchVehicle, fetchOrganizationVehicleSettingsChoices } = this.props;
    if (!this.hasFullData(vehicle)) {
      fetchVehicle(vehicle.id);
    }
    if (this.canManageSettings()) {
      fetchOrganizationVehicleSettingsChoices(this.getOrganizationId());
    }
  }

  componentDidUpdate(prevProps) {
    const { vehicle, storeVehicle } = this.props;
    const { loading } = this.state;
    if (loading && storeVehicle !== prevProps.storeVehicle && storeVehicle
      && storeVehicle.id === vehicle.id && this.hasFullData(storeVehicle)) {
      this.setState({ formData: this.buildFormData(storeVehicle), loading: false });
    }
  }

  // The list serializer has no platform_number; every full payload has it.
  hasFullData = (vehicle) => !!vehicle && vehicle.platform_number !== undefined;

  getOrganizationId = () => {
    const { vehicle, storeVehicle, selectedOrganizationId } = this.props;
    return vehicle.organization_id
      || (storeVehicle && storeVehicle.id === vehicle.id && storeVehicle.organization_id)
      || selectedOrganizationId;
  };

  canManageSettings = () => {
    const { user } = this.props;
    const organizationId = this.getOrganizationId();
    if (!user || !organizationId || organizationId === 'all') return false;
    if (user.is_staff) return true;
    const organization = (user.organizations || []).find((org) => org.id == organizationId);
    return !!organization && (organization.user_role === 'admin' || organization.user_role === 'manager');
  };

  getChoices = () => {
    const { choices } = this.props;
    // The store holds one organization's pick-lists at a time; only trust ours.
    if (!choices || choices.organization_id != this.getOrganizationId()) return null;
    return choices;
  };

  // Either payload may carry the per-organization data; the list row does.
  getOrganizationData = () => {
    const { vehicle, storeVehicle } = this.props;
    if (vehicle && vehicle.organization_data) return vehicle.organization_data;
    if (storeVehicle && storeVehicle.id === vehicle.id && storeVehicle.organization_data) {
      return storeVehicle.organization_data;
    }
    return null;
  };

  showsAssetCategorySelect = () => this.canManageSettings() && !!this.getChoices();

  buildFormData = (vehicle) => ({
    identifier: vehicle.registration_number || '',
    platform_number: vehicle.platform_number || '',
    manufacturer: vehicle.manufacturer || '',
    model_name: vehicle.model_name || '',
    owner_description: vehicle.owner_description || '',
    year_model: vehicle.year_model || '',
    engine_power: vehicle.engine_power || '',
    own_mass: vehicle.own_mass || '',
    length: vehicle.length || '',
    width: vehicle.width || '',
    height: vehicle.height || '',
    // One meter per record for now, as in the add dialog.
    meter: vehicle.tracks_hours ? METER_HOURS : METER_KM,
    asset_category: this.getInitialAssetCategory(vehicle),
  });

  getInitialAssetCategory = (vehicle) => {
    const organizationData = (vehicle && vehicle.organization_data) || this.getOrganizationData();
    return organizationData && organizationData.asset_category ? String(organizationData.asset_category) : '';
  };

  updateFormState = (changed) => {
    const { formData } = this.state;
    this.setState({ formData: { ...formData, [changed.target]: changed.value } });
  };

  setIsValid = (name, isValid) => {
    const { formValid } = this.state;
    this.setState({ formValid: { ...formValid, [name]: isValid } });
  };

  isValid = () => {
    const { vehicle } = this.props;
    const { formData, formValid, loading } = this.state;
    if (loading || !formData) return false;
    if (Object.values(formValid).some((fieldIsValid) => !fieldIsValid)) return false;
    if (!isUnregistered(vehicle)) return true;
    const identifier = (formData.identifier || '').trim();
    return identifier.length > 0 && identifier.length <= 25;
  };

  submitForm = () => {
    const {
      vehicle, updateOrganizationVehicle, selectedWorkshopId, closeModal,
    } = this.props;
    const { formData } = this.state;

    const toNumber = (value) => (value === '' || value === null ? null : Number(value));
    const organizationId = this.getOrganizationId();

    const payload = isUnregistered(vehicle)
      ? {
        equipment: {
          identifier: formData.identifier.trim(),
          // Optional: a blank chassis number is sent as null.
          platform_number: (formData.platform_number || '').trim() || null,
          manufacturer: formData.manufacturer,
          model_name: formData.model_name,
          owner_description: formData.owner_description,
          year_model: toNumber(formData.year_model),
          engine_power: toNumber(formData.engine_power),
          own_mass: toNumber(formData.own_mass),
          length: toNumber(formData.length),
          width: toNumber(formData.width),
          height: toNumber(formData.height),
          // `meter` is the dialog's radio; the API takes the two flags.
          tracks_km: formData.meter === METER_KM,
          tracks_hours: formData.meter === METER_HOURS,
        },
      }
      : {
        year_model: formData.year_model,
        owner_description: formData.owner_description,
      };
    // Sent only when the select was shown: the API leaves an absent field alone.
    if (this.showsAssetCategorySelect()) {
      payload.organization_data = {
        asset_category: formData.asset_category ? Number(formData.asset_category) : null,
      };
    }

    updateOrganizationVehicle(vehicle.id, payload, organizationId, selectedWorkshopId || null);

    closeModal();
  };

  renderInput = (screenSize, field, formData) => (
    <CBInputField
      key={field.name}
      name={field.name}
      type={field.type}
      placeholder={field.placeholder}
      validators={field.validators}
      isValidCallback={field.validators ? this.setIsValid : undefined}
      labelText={field.label}
      labelTextStyle={getStyle(screenSize, styles, 'labelText')}
      inputStyle={getStyle(screenSize, styles, 'input')}
      value={formData[field.name]}
      onChange={this.updateFormState}
    />
  );

  renderFields = (screenSize) => {
    const { t, vehicle } = this.props;
    const { formData } = this.state;

    const yearModel = {
      name: 'year_model',
      type: 'number',
      label: t('yearModelLabel'),
      placeholder: t('yearModelPlaceholder'),
      validators: [getLengthValidator(0, 4)],
    };
    const ownerDescription = {
      name: 'owner_description',
      label: isEquipment(vehicle) ? t('equipmentOwnerDescriptionLabel') : t('vehicleOwnerDescriptionLabel'),
      placeholder: t('vehicleOwnerDescriptionPlaceholder'),
      validators: [getLengthValidator(0, 21)],
    };

    if (!isUnregistered(vehicle)) {
      return (
        <React.Fragment>
          {[yearModel, ownerDescription].map((field) => this.renderInput(screenSize, field, formData))}
          {this.renderAssetCategory(screenSize)}
        </React.Fragment>
      );
    }

    const fields = [
      { name: 'identifier', label: t('equipmentIdentifierLabel') },
      { name: 'platform_number', label: t('equipmentChassisLabel') },
      { name: 'manufacturer', label: t('manufacturerLabel') },
      { name: 'model_name', label: t('modelLabel') },
      ownerDescription,
      yearModel,
      { name: 'engine_power', type: 'number', label: t('enginePowerLabel') },
      { name: 'own_mass', type: 'number', label: t('ownMassLabel') },
      { name: 'length', type: 'number', label: t('lengthLabel') },
      { name: 'width', type: 'number', label: t('widthLabel') },
      { name: 'height', type: 'number', label: t('heightLabel') },
    ];

    return (
      <React.Fragment>
        {fields.map((field) => this.renderInput(screenSize, field, formData))}
        {this.renderAssetCategory(screenSize)}
        <RadioButtonGroup
          name='equipmentMeter'
          value={formData.meter}
          setValue={(value) => this.updateFormState({ target: 'meter', value })}
          options={[
            { value: METER_HOURS, label: t('tracksHoursLabel', { ns: 'GarageView' }) },
            { value: METER_KM, label: t('tracksKmLabel', { ns: 'GarageView' }) },
          ]}
        />
      </React.Fragment>
    );
  };

  renderAssetCategory = (screenSize) => {
    const { t } = this.props;
    const { formData } = this.state;

    if (!this.showsAssetCategorySelect()) {
      const organizationData = this.getOrganizationData();
      const name = organizationData ? organizationData.asset_category_name : null;
      if (!name) return null;
      return (
        <p style={getStyle(screenSize, styles, 'readOnlyRow')}>
          <span className='font-titillium-web-light'>{`${t('assetCategoryLabel')}: `}</span>
          <span className='font-titillium-web-semi-bold'>{name}</span>
        </p>
      );
    }

    const options = [{ label: t('noAssetCategory'), value: '' }].concat(
      (this.getChoices().asset_categories || []).map((category) => ({
        label: category.name, value: String(category.id),
      })),
    );
    return (
      <CBSelectField
        name='asset_category'
        value={formData.asset_category}
        options={options}
        onChange={this.updateFormState}
        labelText={t('assetCategoryLabel')}
        labelTextStyle={getStyle(screenSize, styles, 'labelText')}
        inputStyle={getStyle(screenSize, styles, 'select')}
      />
    );
  };

  render() {
    const { loading } = this.state;
    const { modalIsOpen, closeModal, t, vehicle } = this.props;

    return (
      <CBMediaQuery>
        {screenSize => (
          <CBModal
            modalIsOpen={modalIsOpen}
            afterOpenModal={() => {}}
            closeModal={closeModal}
            title={t('editTechnicalData')}
            cancelText={t('cancel')}
            submitText={t('save')}
            vehicleText={vehicle.registration_number}
            submitForm={this.submitForm}
            isValid={this.isValid()}
          >
            {
              loading ? (
                <div style={{ position: 'relative', minHeight: 200 }}>
                  <CBSpinnerFloating />
                </div>
              ) : (
                <div style={getStyle(screenSize, styles, 'bodyContainer')}>
                  {this.renderFields(screenSize)}
                </div>
              )
            }
          </CBModal>
        )}
      </CBMediaQuery>
    );
  }
}

const styles = {
  default: {
    bodyContainer: {
      display: 'flex',
      flexDirection: 'column',
      padding: '16px 32px',
    },
    labelText: {
      color: 'black',
    },
    input: {
      backgroundColor: '#ebebeb',
      fontSize: 14,
      textAlign: 'center',
      resize: 'none',
      color: 'black',
      borderRadius: 100,
      height: 40,
      border: 'none',
      width: '100%',
    },
    select: {
      backgroundColor: '#ebebeb',
      fontSize: 14,
      textAlign: 'center',
      color: 'black',
      borderRadius: 100,
      height: 40,
      border: 'none',
      width: '100%',
      textAlignLast: 'center',
    },
    readOnlyRow: {
      color: 'black',
      fontSize: 14,
      textAlign: 'center',
      margin: '8px 0',
    },
  },
  small: {
    bodyContainer: {
      display: 'flex',
      flexDirection: 'column',
      padding: '16px 16px',
    },
  },
};

function mapStateToProps(state) {
  return {
    storeVehicle: state.vehicle.vehicle,
    user: state.authUser.user,
    choices: state.organizationVehicleSettings.organizationVehicleSettingsChoices,
    selectedOrganizationId: state.organization.selectedOrganization,
    selectedWorkshopId: state.workshop.selectedWorkshop,
  };
}

export default connect(
  mapStateToProps,
  {
    updateOrganizationVehicle: _updateOrganizationVehicle,
    fetchVehicle: _fetchVehicle,
    fetchOrganizationVehicleSettingsChoices: _fetchOrganizationVehicleSettingsChoices,
  },
)(translate('VehicleDetailsView')(EditTechnicalDataModal));
